import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Notification } from '@/hooks/useNotifications';
import { formatDistanceToNow, isToday, isThisWeek } from 'date-fns';
import { de } from 'date-fns/locale';
import { Check, CheckCheck, Loader2, Users, TrendingUp, Settings, UserPlus, FileCheck } from 'lucide-react';
import { useState } from 'react';

interface NotificationsListProps {
  notifications: Notification[];
  loading: boolean;
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
  onClose: () => void;
}

const getNotificationIcon = (type: string) => {
  switch (type) {
    case 'new_decision':
      return <FileCheck className="h-4 w-4 text-primary" />;
    case 'phase_change':
      return <TrendingUp className="h-4 w-4 text-primary" />;
    case 'member_joined':
      return <UserPlus className="h-4 w-4 text-primary" />;
    case 'team_update':
      return <Users className="h-4 w-4 text-primary" />;
    case 'comment':
      return <Settings className="h-4 w-4 text-primary" />;
    case 'mention':
      return <Users className="h-4 w-4 text-primary" />;
    default:
      return <Settings className="h-4 w-4 text-muted-foreground" />;
  }
};

const groupNotificationsByTime = (notifications: Notification[]) => {
  const today: Notification[] = [];
  const thisWeek: Notification[] = [];
  const older: Notification[] = [];

  notifications.forEach((notification) => {
    const date = new Date(notification.created_at);
    if (isToday(date)) {
      today.push(notification);
    } else if (isThisWeek(date, { weekStartsOn: 1 })) {
      thisWeek.push(notification);
    } else {
      older.push(notification);
    }
  });

  return { today, thisWeek, older };
};

export const NotificationsList = ({
  notifications,
  loading,
  onMarkAsRead,
  onMarkAllAsRead,
  onClose,
}: NotificationsListProps) => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      onMarkAsRead(notification.id);
    }

    // Navigate based on notification type
    if (notification.data?.session_id) {
      navigate(`/app/session/${notification.data.session_id}`);
      onClose();
    }
  };

  const formatTime = (date: string) => {
    try {
      return formatDistanceToNow(new Date(date), { locale: de, addSuffix: true });
    } catch {
      return 'Kürzlich';
    }
  };

  const filteredNotifications = filter === 'unread' 
    ? notifications.filter((n) => !n.is_read)
    : notifications;

  const { today, thisWeek, older } = groupNotificationsByTime(filteredNotifications);
  const unreadCount = notifications.filter((n) => !n.is_read).length;

  if (loading) {
    return (
      <div className="p-4 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const renderNotificationGroup = (groupNotifications: Notification[], title: string) => {
    if (groupNotifications.length === 0) return null;

    return (
      <div>
        <div className="px-4 py-2 bg-muted/30">
          <p className="text-xs font-medium text-muted-foreground">{title}</p>
        </div>
        <div className="divide-y divide-border">
          {groupNotifications.map((notification) => (
            <button
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              className={`w-full p-4 text-left hover:bg-accent/50 transition-colors ${
                !notification.is_read ? 'bg-accent/20' : ''
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5">
                  {getNotificationIcon(notification.type)}
                </div>
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-sm text-foreground">
                      {notification.title}
                    </p>
                    {!notification.is_read && (
                      <span className="h-2 w-2 rounded-full bg-primary flex-shrink-0" />
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {notification.message}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatTime(notification.created_at)}
                  </p>
                </div>
                {!notification.is_read && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMarkAsRead(notification.id);
                    }}
                    className="h-8 w-8 p-0 flex-shrink-0"
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h3 className="font-semibold text-foreground">Benachrichtigungen</h3>
        {unreadCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onMarkAllAsRead}
            className="text-xs"
          >
            <CheckCheck className="h-3 w-3 mr-1" />
            Alle als gelesen
          </Button>
        )}
      </div>

      <div className="flex gap-2 p-2 border-b border-border">
        <Button
          variant={filter === 'all' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setFilter('all')}
          className="flex-1 h-8"
        >
          Alle
        </Button>
        <Button
          variant={filter === 'unread' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setFilter('unread')}
          className="flex-1 h-8"
        >
          Ungelesen {unreadCount > 0 && `(${unreadCount})`}
        </Button>
      </div>

      {filteredNotifications.length === 0 ? (
        <div className="p-8 text-center text-sm text-muted-foreground">
          {filter === 'unread' ? 'Keine ungelesenen Benachrichtigungen' : 'Keine Benachrichtigungen'}
        </div>
      ) : (
        <ScrollArea className="h-[400px]">
          {renderNotificationGroup(today, 'Heute')}
          {renderNotificationGroup(thisWeek, 'Diese Woche')}
          {renderNotificationGroup(older, 'Älter')}
        </ScrollArea>
      )}

      <div className="p-3 border-t border-border">
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-center text-xs"
          onClick={() => {
            onClose();
            setTimeout(() => navigate('/settings/notifications'), 100);
          }}
        >
          <Settings className="h-3 w-3 mr-2" />
          Benachrichtigungs-Einstellungen
        </Button>
      </div>
    </div>
  );
};
